import {JSX, useEffect, useMemo, useState} from "react";
import {Button, Grid, Link, Typography} from "@mui/material";
import {FileInputEntry, IFileData} from "@shared/specialComponents/FileInputEntry.tsx";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {Publish} from "@mui/icons-material";
import {usePublic} from "@shared/hooks/usePublic.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {IOperationResponse} from "@localization/siteMap/siteMapInterfaces.ts";
import {IDataNoPageDto} from "@models/shared/IDataDto.ts";
import {PermissaoAplicacao} from "@models/seguranca/permissaoApp.ts";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";

export interface ImportDataComponentProps<T, TOutput> {
    onCreate?: (v?: TOutput[]) => void
    importMethod: (v: IDataNoPageDto<T[]>) => Promise<IOperationResponse<TOutput[]>>
    treatPreviewData?: (v: T) => T
    previewGrid: GridDef<T>[],
    filename?: string,
    templateUrl: string,
    sliceCount?: number,
    permissao?: PermissaoAplicacao | PermissaoAplicacao[]
}

interface GridDef<T> {
    field: keyof T,
    headerName: string,
    width: number,
    renderCell?: (params: any) => string | JSX.Element | null,
}


export function ImportDataComponent<T, TOutput>(props: ImportDataComponentProps<T, TOutput>) {
    const {importMethod, filename, templateUrl, treatPreviewData, previewGrid, onCreate} = props
    const {importFromFile} = usePublic()
    const {createAlertFromResponse} = useAlert()
    const [fileData, setFileData] = useState<IFileData[]>([])
    const [fileExample, setFileExample] = useState<string | undefined>(undefined)
    const [preview, setPreview] = useState<T[]>([])
    const [isImporting, setIsImporting] = useState<boolean>(false)

    const sliceCount = useMemo(() => {
        return props.sliceCount ?? 250
    }, [props.sliceCount])

    const importing = useMemo(() => {
        return [...preview.slice(0, sliceCount)]
    }, [preview])

    useEffect(() => {
        if (!isImporting) {
            return;
        }

        if (importing.length > 0) {
            importMethod({data: importing}).then(x => {
                createAlertFromResponse(x)
                if (x.status == 'Success') {

                    if (onCreate != null)
                        onCreate(x.items)
                }
            }).finally(() => {
                setPreview([...preview.slice(sliceCount)])
            })
        } else {
            setIsImporting(false)
        }
    }, [importing, isImporting]);

    useEffect(() => {
        fetch(templateUrl).then(x => x.blob()).then(x => {
            const url = URL.createObjectURL(x)
            setFileExample(url)
        })

        return () => {
            if (fileExample) {
                URL.revokeObjectURL(fileExample)
            }
        }
    }, []);
    const datagridColumns: GridColDef[] = previewGrid.map(x => {
        return {
            field: x.field,
            headerName: x.headerName,
            width: x.width,
            renderCell: x.renderCell
        } as GridColDef
    })


    useEffect(() => {
        if (fileData?.length > 0) {
            importFromFile<T>(fileData[0]).then(x => {
                try {
                    setPreview(x.map((x) => {
                        if (treatPreviewData != null) {
                            return treatPreviewData(x)
                        }
                        return x
                    }))
                } catch {
                    setPreview([])
                }
            })
        } else {
            setPreview([])
        }
        return;

    }, [fileData])

    const validFilename = useMemo(() => {
        if (filename == null || filename.length < 3) {
            return 'import-exemplo.csv'
        }
        return filename;
    }, [filename]);

    return (
        <DefaultContainer permissaoNecessaria={props.permissao}>
            <Grid size={12} container>
                <Grid>
                    <Typography variant={'caption'}>
                        Importe um arquivo CSV com os dados dos recursos baixe o
                        <Typography
                            variant={'caption'}
                            component={Link}
                            href={fileExample || '#'}
                            download={validFilename}
                            sx={{cursor: 'pointer', textDecoration: 'underline', mx: 0.5}}
                        >
                            arquivo exemplo
                        </Typography>
                        e preencha os campos.
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <Typography variant={'subtitle2'} color={'error'} textAlign={'center'}>Não exclua ou renomeie o
                        cabeçalho!</Typography>
                </Grid>
            </Grid>
            <Grid container justifyContent={'space-between'} size={12}>
                <Grid size={12}>
                    <FileInputEntry fileType={'csv'} files={fileData} onChange={(v) => {
                        setFileData(v ?? [])
                    }}/>
                </Grid>
                <Grid size={12} container justifyContent={'flex-end'} alignItems={'center'}>
                    <Button disabled={preview.length == 0}
                            endIcon={<Publish/>}
                            loading={isImporting}
                            onClick={() => {
                                setIsImporting(true)
                            }}
                    >Importar</Button>
                </Grid>
            </Grid>
            {preview.length > 0 &&
                <Grid size={12}>
                    <DataGrid columns={datagridColumns}
                              rows={preview}
                              getRowId={(row) => (row as any).id || JSON.stringify(row)}
                              initialState={{
                                  pagination: {
                                      paginationModel: {
                                          pageSize: 10
                                      }
                                  }
                              }}/>
                </Grid>}

        </DefaultContainer>
    )
}